import { DocumentNode, gql } from '@apollo/client';
import { Image, Link, Text, VStack } from '@chakra-ui/react';
import * as React from 'react';

import { sponsor_link_clicked } from '@/analytics';
import { IRequestDemoView } from '@/types/analytics';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';
import { getExternalUrl } from '@/utilities/getExternalUrl';

import { ConferenceBoothMobileSponsors_BoothFragment } from './graphql-types/ConferenceBoothMobileSponsors_BoothFragment';

export const ConferenceBoothMobileSponsors: React.FC<{
  conferenceBoothData?: ConferenceBoothMobileSponsors_BoothFragment;
  origin: {
    conferenceBoothId?: string | undefined;
    sponsoredEventId?: string | undefined;
    sponsoredConferenceSessionId?: string | undefined;
    hostedEventId?: string | undefined;
    sponsorPage?: boolean | undefined;
  };
  analytics: {
    view: IRequestDemoView;
    tier: string;
  };
}> & { boothFragment: DocumentNode } = ({
  conferenceBoothData,
  origin,
  analytics,
}) => {
  const specialOffer = conferenceBoothData?.specialOffer;
  const ad = specialOffer
    ? specialOffer
    : conferenceBoothData?.hostOrganization.sponsorship?.ad;
  const { adImage, title, body, url, callToAction } = ad || {};
  const fullUrl = getExternalUrl(url ?? undefined);

  if (!ad || !conferenceBoothData?.hostOrganization) {
    return null;
  }

  return (
    <VStack spacing="2">
      {ad && (
        <VStack
          w="full"
          backgroundColor="white"
          p="4"
          boxShadow="md"
          spacing="4"
        >
          {adImage?.mobileImageProxy ? (
            <Image
              src={adImage.mobileImageProxy}
              w="100px"
              borderRadius="lg"
              alt={`Sponsor promotion for ${conferenceBoothData.hostOrganization.name}`}
            />
          ) : (
            <ImageFallback borderRadius="lg" />
          )}

          <Text fontWeight="semibold" fontSize="xl">
            {conferenceBoothData.hostOrganization.name}
          </Text>
          <Text fontWeight="semibold" fontSize="md">
            {title}
          </Text>
          <Text>{body}</Text>
          <RequestDemoButton
            options={{
              organizationId: conferenceBoothData.hostOrganization._id,
              origin,
              registerInterestTitle:
                ad.registerInterestTitle ||
                conferenceBoothData.hostOrganization.sponsorship
                  ?.requestDemoTitle ||
                '',
              analytics,
            }}
            w="full"
          />

          {fullUrl && callToAction && (
            <Link
              href={fullUrl}
              isExternal
              onClick={() => {
                sponsor_link_clicked({
                  sponsor_id: conferenceBoothData.hostOrganization._id,
                  sponsor_name: conferenceBoothData.hostOrganization.name,
                  sponsor_tier: analytics.tier,
                  sponsor_link_type: 'website',
                  sponsor_destination_url: fullUrl,
                  sponsor_cta_text: callToAction,
                  sponsor_interaction_source: 'event',
                  location_organization_id:
                    conferenceBoothData.hostOrganization._id,
                  event_backed_sponsor_interaction: true,
                  event_sponsor_id: conferenceBoothData.hostOrganization._id,
                  event_session_id: origin.conferenceBoothId,
                });
              }}
            >
              {callToAction}
            </Link>
          )}
        </VStack>
      )}
    </VStack>
  );
};

ConferenceBoothMobileSponsors.boothFragment = gql`
  fragment ConferenceBoothMobileSponsors_BoothFragment on ConferenceBooth {
    _id
    specialOffer {
      callToAction
      body
      url
      title
      adImage {
        mobileImageProxy: imageProxy(input: { h: 144, w: 146, fit: "crop" })
      }
      registerInterestTitle
    }
    hostOrganization {
      _id
      name
      sponsorship {
        requestDemoTitle
        ad {
          _id
          callToAction
          body
          url
          title
          adImage {
            mobileImageProxy: imageProxy(input: { h: 144, w: 146, fit: "crop" })
          }
          registerInterestTitle
        }
      }
    }
  }
`;
